<template>
    <div class="color-input-wrapper">
        <div class="color-input-container">
            <button
                type="button"
                class="color-preview"
                :style="{
                    backgroundColor: value || '#ffffff',
                }"
                :disabled="disabled"
                :aria-label="`Open color picker for ${label || 'color'}`"
                @click="onColorPickerClick"
            ></button>

            <v-text-field
                ref="textField"
                :value="value"
                :label="label"
                :placeholder="placeholder"
                :dense="dense"
                :outlined="outlined"
                :disabled="disabled"
                :hide-details="hide_details"
                :rules="color_rules"
                validate-on-blur
                :aria-label="`${label || 'Color'} value`"
                class="ml-3"
                @input="onInputChange"
                @blur="onBlur"
            />
        </div>

        <color-picker-dialog
            :visible.sync="color_picker_dialog"
            :color_value.sync="color_picker_value"
            :show_conversion_note="show_conversion_note"
            @cancel="color_picker_dialog = false"
            @select="onColorSelected"
        />
    </div>
</template>

<script>
import { parseAnyColorValueToRgba, isValidHex, isValidRgb, isValidRgba } from '@/js/color.js';
import ColorPickerDialog from './color-picker-dialog.vue';

export default {
    name: 'ColorInput',

    components: {
        ColorPickerDialog,
    },

    props: {
        value: {
            type: String,
            default: '',
        },

        label: {
            type: String,
            default: 'Color',
        },

        placeholder: {
            type: String,
            default: '',
        },

        dense: {
            type: Boolean,
            default: false,
        },

        outlined: {
            type: Boolean,
            default: false,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        hide_details: {
            type: [Boolean, String],
            default: "auto",
        },

        show_conversion_note: {
            type: Boolean,
            default: false,
        },

        /**
         * Optional sessionStorage key to automatically persist the color value
         */
        storage_key: {
            type: String,
            default: null,
        },

        /**
         * Whether the field is required
         */
        required: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            color_picker_dialog: false,
            color_picker_value: { r: 255, g: 255, b: 255, a: 1 },
            pending_value: null, // temporarily holds typed value until blur
            touched: false,
        };
    },

    computed: {
        color_rules() {
            return [
                v => {
                    if (!this.touched && !v) return true;
                    if (this.required && !v) {
                        return 'Color is required';
                    }
                    if (!v) return true;
                    if (isValidHex(v) || isValidRgb(v) || isValidRgba(v)) {
                        return true;
                    }
                    return 'Invalid color format. Use hex, rgb, or rgba.';
                }
            ];
        },

        isValid() {
            if (!this.touched && !this.value) return true;
            if (this.required && !this.value) return false;
            if (!this.value) return true;
            return isValidHex(this.value) || isValidRgb(this.value) || isValidRgba(this.value);
        },
    },

    watch: {
        isValid: {
            handler(newVal) {
                this.$emit('update:valid', newVal);
            },
            immediate: true,
        },

        value(newVal) {
            // sync pending_value when value changes externally (e.g., from parent watcher)
            this.pending_value = newVal;
        },
    },

    methods: {
        onColorPickerClick() {
            if (this.disabled) return;

            if (!this.value) {
                this.color_picker_value = { r: 255, g: 255, b: 255, a: 1 };
                this.color_picker_dialog = true;
                return;
            }

            const parsedColor = parseAnyColorValueToRgba(this.value);
            if (parsedColor) {
                this.color_picker_value = { ...parsedColor, a: parsedColor.a || 1 };
            } else {
                this.color_picker_value = { r: 255, g: 255, b: 255, a: 1 };
            }

            this.color_picker_dialog = true;
        },

        onColorSelected(color_value) {
            if (!this.touched) {
                this.touched = true;
            }
            
            const rgba = color_value || this.color_picker_value;
            // for consistency from color picker
            const formatted_color_value = `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;

            if (this.storage_key) {
                window.sessionStorage.setItem(this.storage_key, formatted_color_value);
            }

            this.$emit('input', formatted_color_value);
            this.$emit('change', formatted_color_value);
            this.color_picker_dialog = false;
            
            this.pending_value = formatted_color_value;
            
            this.$nextTick(() => {
                if (this.$refs.textField) {
                    this.$refs.textField.resetValidation();
                }
            });
        },

        onInputChange(value) {
            if (!this.touched) {
                this.touched = true;
            }
            // store pending value but don't validate or emit until blur
            this.pending_value = value;
        },

        onBlur() {
            if (!this.touched) {
                this.touched = true;
            }
            
            let value = this.pending_value;

            // auto-add "#" if they entered 3 or 6 hex digits without it
            if (value && /^[0-9A-Fa-f]{3}$/.test(value)) {
                value = `#${value}`;
                this.pending_value = value;
            } else if (value && /^[0-9A-Fa-f]{6}$/.test(value)) {
                value = `#${value}`;
                this.pending_value = value;
            }

            // Validate that it's a recognized color format
            if (value && (isValidHex(value) || isValidRgb(value) || isValidRgba(value))) {
                let formattedValue = value;

                // auto-capitalize hex values
                if (isValidHex(value)) {
                    formattedValue = value.toUpperCase();
                    
                    // if it's a 3-digit hex where all digits are the same (e.g., #AAA, #333),
                    // expand to 6-digit format (e.g., #AAAAAA, #333333)
                    if (formattedValue.length === 4) {
                        const char = formattedValue[1];
                        if (formattedValue[2] === char && formattedValue[3] === char) {
                            formattedValue = `#${char}${char}${char}${char}${char}${char}`;
                        }
                    }
                }
                // only format rgba values for consistency - preserve hex and rgb as-is
                else if (isValidRgba(value)) {
                    const parsedColor = parseAnyColorValueToRgba(value);
                    if (parsedColor) {
                        formattedValue = `rgba(${parsedColor.r}, ${parsedColor.g}, ${parsedColor.b}, ${parsedColor.a})`;
                    }
                }

                if (this.storage_key) {
                    window.sessionStorage.setItem(this.storage_key, formattedValue);
                }

                this.$emit('input', formattedValue);
                this.$emit('change', formattedValue);
                
                // reset validation after successful formatting to clear any error messages
                this.$nextTick(() => {
                    if (this.$refs.textField) {
                        this.$refs.textField.resetValidation();
                    }
                });
            } else if (value !== this.value) {
                // if invalid, revert to previous valid value
                this.pending_value = this.value;
                // force the text field to update to the valid value
                this.$emit('input', this.value);
            }
        },
    },
};
</script>

<style scoped lang="scss">
.color-input-wrapper {
    width: 100%;
}

.color-input-container {
    display: flex;
}

.color-preview {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: var(--border-radius-default);
    border: 1px solid rgba(0, 0, 0, 0.1);
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    transition: border-color 0.2s ease;

    &:hover:not(:disabled) {
        border-color: rgba(0, 0, 0, 0.3);
    }

    &:focus:not(:disabled) {
        outline: 2px solid var(--v-primary-base);
        outline-offset: 2px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
}

:deep(.v-input__slot) {
    margin-bottom: 0;
}
:deep(.v-text-field__details) {
    padding-left: 0 !important;
    margin-bottom: 0 !important
}
</style>
